/**
 * https://developers.surfboardpayments.com/api/orders?lang=Node.js#Create-New-Order
 */
import { SurfboardPhoneNumber } from './surfboard-order.interface';

export interface SurfboardOrderCarbon {
  /** ID of the checkout or payment terminal */
  "terminal$id": string;
  /** External reference ID for the order */
  referenceId?: string;
  /** Additional comments or notes for the order */
  comments?: string;
  /** Customer details for the order */
  customer?: SurfboardOrderCustomerInformationCarbon;
  /** Billing contact and address */
  billing?: SurfboardBillingCarbon;
  /** Shipping contact and address */
  shipping?: SurfboardShippingCarbon;
  /** List of items in the order */
  orderLines: SurfboardOrderLineCarbon[];
  /** Total amount details of the order */
  totalOrderAmount?: SurfboardTotalOrderAmountDetailsCarbon;
  /** Order date in ISO 8601 format */
  date?: string;
  /** Arbitrary key-value style metadata items */
  metadata?: Record<string, unknown>;
  /** Order-level adjustments */
  adjustments?: SurfboardAdjustmentCarbon[];
  /** Advanced order/payment control configuration */
  controlFunctions?: SurfboardControlFunctionsCarbon;
}

// --- Customer & Identity ---

export interface SurfboardOrderCustomerInformationCarbon {
  /** Unique identifier for the customer */
  customerId?: string;
  /** Personal information of the customer */
  person?: {
    name?: SurfboardNameCarbon;
    email?: string;
    phoneNumber: SurfboardPhoneNumber;
    birthDate?: string;
    personalNumber?: string;
    coordinationNumber?: string;
    countryResidence?: string;
    gdNumber?: string;
  };
  /** Company information for corporate purchases */
  company?: {
    vatId?: string;
    poDetails?: string;
  };
}

export interface SurfboardNameCarbon {
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

// --- Contact & Address ---

export interface SurfboardBillingCarbon {
  name?: SurfboardNameCarbon;
  email?: string;
  phoneNumber?: SurfboardPhoneNumber;
  address?: SurfboardAddressCarbon;
}

export interface SurfboardShippingCarbon {
  name?: SurfboardNameCarbon;
  email?: string;
  phoneNumber?: SurfboardPhoneNumber;
  address?: SurfboardAddressCarbon;
}

export interface SurfboardAddressCarbon {
  careOf?: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  city: string;
  postalCode: string;
  /** Country code (ISO 3166-1 alpha-2) */
  countryCode: string;
}

// --- Order Lines & Amounts ---

export interface SurfboardOrderLineCarbon {
  id: string;
  externalItemId?: string;
  name: string;
  categoryId?: string;
  description?: string;
  /** A negative value indicates a refund */
  quantity: number;
  amount: {
    regular: number;
    campaign?: number;
    shipping?: number;
    total: number;
    /** Currency (numeric ISO 4217 as string) */
    currency: string;
    tax?: SurfboardTaxCarbon[];
  };
  unit?: string;
  gtin?: string;
  imageUrl?: string;
  brand?: string;
  purchaseOrderId?: string;
  promotionApplied?: string;
  metadata?: Record<string, unknown>;
  storeId?: string;
}

export interface SurfboardTotalOrderAmountDetailsCarbon {
  regular: number;
  campaign?: number;
  total: number;
  /** Currency (numeric ISO 4217 as string) */
  currency: string;
  tax?: SurfboardTaxCarbon[];
}

export interface SurfboardTaxCarbon {
  amount: number;
  percentage: number;
  type: string;
}

export interface SurfboardAdjustmentCarbon {
  type: string;
  /** Adjustment value (as string per MD) */
  value: string;
  /** Arbitrary key-value style metadata items */
  metadata?: string;
}

// --- Control Functions & Payment Options ---

export interface SurfboardControlFunctionsCarbon {
  cancelPreviousPendingOrder?: boolean;
  orderLineLevelCalculation?: boolean;
  /** External service providers participating in the transaction */
  serviceProviders?: SurfboardServiceProviderCarbon[];
  /** Parameters for initiating payment */
  initiatePaymentsOptions?: SurfboardInitiatePaymentsOptionsCarbon;
  tipsMode?: 'STANDARD' | 'ROUNDUP' | 'NONE';
  cardStatementDescriptor?: string;
  includeAdjustmentsForRefund?: boolean;
  lockToPaymentMethods?: string[];
  tapBeforeAmount?: boolean;
  delayCapture?: boolean;
  delayPayout?: string;
  fulfillmentDate?: string;
  callBackUrl?: string;
  throwErrorIfTerminalInactiveFor?: number;
  clearingCutOff?: string;
  currency?: string;
  authMode?: 'PRE-AUTH' | 'AUTH';
  /** Receipt handling options */
  receipt?: SurfboardReceiptCarbon;
  /** Records of additional fees or charges */
  surcharges?: SurfboardSurchargesCarbon;
  readTags?: string;
  online?: SurfboardOnlineDetailsCarbon;
}

export interface SurfboardServiceProviderCarbon {
  serviceProviderId: string;
  amount?: {
    percentage?: string;
    fixed?: string;
    adjustmentTypes?: string[];
  };
}

export interface SurfboardInitiatePaymentsOptionsCarbon {
  paymentMethod?: string;
  amount?: number;
  paymentMethodParams?: SurfboardPaymentMethodParamsCarbon;
  serialSplitPaymentsMode?: number[];
  refundProcessingParams?: {
    purchasePaymentId?: string;
    refundReason?: string;
    otherReason?: string;
  };
}

export interface SurfboardPaymentMethodParamsCarbon {
  tokenId?: string;
  bic?: string;
  swishMode?: string;
  payeePhoneNumber?: SurfboardPhoneNumber;
  giftCard?: {
    giftCardId?: string;
    giftCardRedemptionMode?: string;
  };
  invoice?: {
    invoiceDistribution?: string;
    contractReference?: string;
    debtCollectionEnabled?: boolean;
    dueDate?: string;
    invoicePaymentMethods?: string[];
    reminder?: boolean;
  };
}

export interface SurfboardReceiptCarbon {
  autoPrintReceipt?: boolean;
  autoSendReceiptIfEmailAvailable?: boolean;
  sequenceNumber?: string;
  cashRegisterName?: string;
  controlUnitSerialNumber?: string;
  receiptRedirectURL?: string;
  cashierName?: string;
  promotionsToDisplay?: any[];
}

export interface SurfboardSurchargesCarbon {
  domestic?: SurfboardSurchargeCategoryCarbon;
  international?: SurfboardSurchargeCategoryCarbon;
  eea?: SurfboardSurchargeCategoryCarbon;
}

export interface SurfboardSurchargeCategoryCarbon {
  consumer?: SurfboardSurchargeTypeCarbon;
  corporate?: SurfboardSurchargeTypeCarbon;
}

export interface SurfboardSurchargeTypeCarbon {
  debit?: {
    fixedAmount?: number;
    fixedPercentage?: number;
  };
  credit?: {
    fixedAmount?: number;
    fixedPercentage?: number;
  };
}

export interface SurfboardOnlineDetailsCarbon {
  enforceTokenization?: boolean;
  paymentPageValidFor?: string;
  enforce3DSecure?: boolean;
  redirectUrl?: string;
}
